// Use only pure local helpers so the Canvas API shares the score shown in the UI and CSV export.
import { QuizRubricType } from './quizConstants';
import { getQuizOverview, getQuizResponse } from './quizOverview';
import { isUnscoredRubric } from './rubricScoring';

const number = (value) => {
    if (!['number', 'string'].includes(typeof value) || String(value).trim() === '') return null;
    const result = Number(value);
    return Number.isFinite(result) ? result : null;
};

const sum = (values) =>
    values.every((value) => value != null) ? values.reduce((total, value) => total + value, 0) : null;

const confirmed = (submission) =>
    Boolean(submission?.grading && (!submission.grading_status || submission.grading_status === 'confirmed'));

export const isQuizCoursework = (coursework, submission) =>
    coursework?.type === 'quiz' ||
    submission?.type === 'quiz' ||
    submission?.quiz_id != null ||
    Array.isArray(coursework?.questions);

export const getQuizCriterionMaxPoints = (criterion) => {
    const rates = (criterion?.options ?? []).map(({ rate }) => number(rate)).filter((rate) => rate != null);
    return rates.length ? Math.max(0, ...rates) : null;
};

export const getQuizCriterionScore = (submission, criterion, { suggested = false } = {}) => {
    if (!criterion || (!suggested && !confirmed(submission))) return null;
    if (!suggested && submission.grade_overrides?.[criterion.id] != null) {
        return number(submission.grade_overrides[criterion.id]);
    }
    const optionId = suggested
        ? submission.ai?.grading?.[criterion.id]?.optionId
        : submission.grading?.criteria?.[criterion.id];
    if (optionId == null || optionId === '') return null;
    return number(criterion.options?.find(({ id }) => String(id) === String(optionId))?.rate);
};

export const getQuizRubricScore = (submission, quiz, { suggested = false } = {}) => {
    if (quiz?.ai_grading_enabled === false || isUnscoredRubric(quiz) || (!suggested && !confirmed(submission))) {
        return null;
    }
    if (!suggested && submission.grade_overrides?.total != null) return number(submission.grade_overrides.total);
    const criteria = quiz?.rubric?.criteria ?? [];
    return criteria.length
        ? sum(criteria.map((criterion) => getQuizCriterionScore(submission, criterion, { suggested })))
        : null;
};

const scale = (earned, possible, points) => {
    if (earned == null || possible == null) return null;
    return possible ? (earned / possible) * points : points === 0 && earned === 0 ? 0 : null;
};

// Question scores use quiz points; criterion scores retain their rubric scale.
export const getQuizQuestionScore = (submission, question, { suggested = false } = {}) => {
    const criteria = question.criteria ?? [];
    if (!criteria.length) return null;
    return scale(
        sum(criteria.map((criterion) => getQuizCriterionScore(submission, criterion, { suggested }))),
        sum(criteria.map(getQuizCriterionMaxPoints)),
        question.points
    );
};

// Final totals combine the converted written-response grade and imported LMS scores.
export const getQuizGradeSnapshot = (submission, quiz, { suggested = false } = {}) => {
    if (
        !submission ||
        quiz?.ai_grading_enabled === false ||
        isUnscoredRubric(quiz) ||
        (!suggested && !confirmed(submission))
    ) {
        return null;
    }
    const overview = getQuizOverview(quiz);
    if (!overview.questions.length) return null;
    const criteria = quiz?.rubric?.criteria ?? [];
    let writtenScore = 0;
    if (overview.timelyQuestions.length) {
        if (quiz.rubric_type === QuizRubricType.QUESTION && (suggested || submission.grade_overrides?.total == null)) {
            const criteriaById = new Map(criteria.map((criterion) => [String(criterion.id), criterion]));
            writtenScore = sum(
                overview.timelyQuestions.map((question) =>
                    getQuizQuestionScore(
                        submission,
                        {
                            ...question,
                            criteria: [...new Set(question.criterionIds.map(String))].map((id) => criteriaById.get(id)),
                        },
                        { suggested }
                    )
                )
            );
        } else {
            writtenScore = scale(
                getQuizRubricScore(submission, quiz, { suggested }),
                criteria.length ? sum(criteria.map(getQuizCriterionMaxPoints)) : null,
                overview.timelyPoints
            );
        }
    }
    const autoScore = sum(
        overview.lmsQuestions.map(({ id, points }) => {
            const score = number(getQuizResponse(submission, id)?.score);
            return score ?? (points === 0 ? 0 : null);
        })
    );
    if (writtenScore == null || autoScore == null) return null;
    const grade = writtenScore + autoScore;
    return { grade, max_grade: overview.totalPoints, display_grade: `${grade}/${overview.totalPoints}` };
};

export const getCanvasQuizGrade = (snapshot, pointsPossible) => {
    const maximum = number(pointsPossible);
    if (!snapshot || maximum == null || maximum < 0) return null;
    const grade = scale(snapshot.grade, snapshot.max_grade, maximum);
    return Number.isFinite(grade) ? grade : null;
};

// Review previews fill untouched criteria from AI without making the draft a confirmed grade.
export const getQuizPreviewGradeSnapshot = (submission, quiz, grading, gradeOverrides) =>
    getQuizGradeSnapshot(
        {
            ...submission,
            grading_status: 'confirmed',
            grading: {
                ...grading,
                criteria: {
                    ...Object.fromEntries(
                        Object.entries(submission?.ai?.grading ?? {}).map(([id, selection]) => [
                            id,
                            selection?.optionId,
                        ])
                    ),
                    ...grading?.criteria,
                },
            },
            grade_overrides: gradeOverrides ?? {},
        },
        quiz
    );
